/**
 * First-name display formatting.
 *
 * @module nameFormat
 */

/** Matches any uppercase letter in any script (used to detect deliberate casing) */
const HAS_UPPERCASE = /\p{Lu}/u;

/**
 * Trim a first name and capitalize its first letter **only when the input
 * contains no uppercase letter at all**.
 *
 * Mirrors `normalizeFirstName` in `backend/src/utils/nameValidator.js` — the
 * backend owns what gets stored, this copy exists so orientation can render the
 * captured name correctly in the same tick instead of waiting on (or depending
 * on) the persist round-trip. Keep the two rules identical.
 *
 * Narrow by design: fixes `doug` → `Doug` (conversational input, no
 * autocapitalize) while leaving casing the user chose on purpose alone — `JD`,
 * `McKenna`, `O'Brien`, `DeAndre` all survive, where a lowercase-then-capitalize
 * rule would mangle every one. Scripts without case are no-ops.
 *
 * @param {string} value - Raw (untrimmed) first name
 * @returns {string} Trimmed, conditionally capitalized name; non-strings pass through
 */
export function normalizeFirstName(value) {
  if (typeof value !== 'string') {
    return value;
  }
  const trimmed = value.trim();
  if (trimmed.length === 0 || HAS_UPPERCASE.test(trimmed)) {
    return trimmed;
  }
  // Destructure iterates by code point, so astral first characters stay intact.
  const [first, ...rest] = trimmed;
  return first.toUpperCase() + rest.join('');
}
